import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CashBookEntryInput,
  CashBookEntryResponse,
  CashBookMonthDetail,
  CashBookMonthResponse,
  CreatePaymentInput,
  InitCashBookMonthInput,
  ItemResponse,
  ListResponse,
  OpenItemDirection,
  OpenItemResponse,
  PaymentResponse,
  UpdateCashBookEntryInput,
  UpdateCashBookMonthInput,
} from '@proerp/shared';
import { openFilePreview } from '@proerp/ui';
import { apiRequest } from '@/lib/apiClient';

const OPEN_ITEMS_KEY = ['accounting', 'open-items'] as const;

/** Zahlungs-Endpunkt je Richtung: Debitor (Rechnung) vs. Kreditor (Eingangsrechnung). */
function paymentsBase(documentId: string, direction: OpenItemDirection): string {
  return direction === 'PAYABLE'
    ? `/v1/accounting/payables/${documentId}/payments`
    : `/v1/accounting/invoices/${documentId}/payments`;
}

export function useOpenItems() {
  return useQuery({
    queryKey: OPEN_ITEMS_KEY,
    queryFn: () => apiRequest<ListResponse<OpenItemResponse>>('/v1/accounting/open-items'),
  });
}

export function usePayments(documentId: string, direction: OpenItemDirection = 'RECEIVABLE') {
  return useQuery({
    queryKey: ['accounting', 'payments', documentId],
    queryFn: () => apiRequest<ListResponse<PaymentResponse>>(paymentsBase(documentId, direction)),
    enabled: Boolean(documentId),
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/accounting/payments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
      void queryClient.invalidateQueries({ queryKey: ['sales-documents'] });
    },
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      documentId,
      direction,
      input,
    }: {
      documentId: string;
      direction: OpenItemDirection;
      input: CreatePaymentInput;
    }) =>
      apiRequest<ItemResponse<PaymentResponse>>(paymentsBase(documentId, direction), {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounting'] });
      // Beleg-Status kann sich geändert haben (Verkauf/Einkauf).
      void queryClient.invalidateQueries({ queryKey: ['sales-documents'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

// ── Kassabuch (digitales Kassenbuch, Monatsansicht) ──────────────────────────

const CASH_BOOK_KEY = ['accounting', 'cash-book'] as const;

export function useCashBookMonth(year: number, month: number) {
  return useQuery({
    queryKey: [...CASH_BOOK_KEY, year, month],
    queryFn: () =>
      apiRequest<ItemResponse<CashBookMonthDetail>>(`/v1/cash-book?year=${year}&month=${month}`),
  });
}

function useCashBookMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CASH_BOOK_KEY }),
  });
}

export function useInitCashBookMonth() {
  return useCashBookMutation((input: InitCashBookMonthInput) =>
    apiRequest<ItemResponse<CashBookMonthResponse>>('/v1/cash-book/months', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateCashBookMonth() {
  return useCashBookMutation(({ id, input }: { id: string; input: UpdateCashBookMonthInput }) =>
    apiRequest<ItemResponse<CashBookMonthResponse>>(`/v1/cash-book/months/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  );
}

export function useAddCashBookEntry() {
  return useCashBookMutation(({ monthId, input }: { monthId: string; input: CashBookEntryInput }) =>
    apiRequest<ItemResponse<CashBookEntryResponse>>(`/v1/cash-book/months/${monthId}/entries`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateCashBookEntry() {
  return useCashBookMutation(({ id, input }: { id: string; input: UpdateCashBookEntryInput }) =>
    apiRequest<ItemResponse<CashBookEntryResponse>>(`/v1/cash-book/entries/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  );
}

export function useDeleteCashBookEntry() {
  return useCashBookMutation((id: string) =>
    apiRequest<{ data: { id: string } }>(`/v1/cash-book/entries/${id}`, { method: 'DELETE' }),
  );
}

/** Kassabuch-PDF des Monats im Fenster anzeigen (ADR 0075). */
export function openCashBookPdf(monthId: string, label: string): void {
  openFilePreview({
    path: `/v1/cash-book/months/${monthId}/pdf`,
    title: `Kassabuch ${label}`,
    filename: `Kassabuch-${label.replace('/', '-')}.pdf`,
  });
}
